import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface PressArticleRow {
  id: string;
  tournament_id: string | null;
  source: string;
  title: string;
  date: string | null;
  quote: string;
  link: string;
  image_url: string | null;
}

const VISIBLE = 3;

function PressSection() {
  const [articles, setArticles] = useState<PressArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('press_articles')
        .select('*')
        .order('date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching press articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(articles.length / VISIBLE);
  const visibleArticles = articles.slice(page * VISIBLE, page * VISIBLE + VISIBLE);

  const prev = useCallback(() => {
    setDirection(-1);
    setPage(p => Math.max(0, p - 1));
  }, []);

  const next = useCallback(() => {
    setDirection(1);
    setPage(p => Math.min(totalPages - 1, p + 1));
  }, [totalPages]);

  const variants = {
    enter: (dir: number) => ({ x: dir * 60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir * -60, opacity: 0 }),
  };

  return (
    <section className="py-24 bg-golf-sand">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto"
        >
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-golf-gray mb-4">
              Press Coverage
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              What the media is saying about Ibom Open
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
            </div>
          ) : articles.length > 0 ? (
            <div>
              <div className="relative overflow-hidden">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={page}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: 'easeInOut' }}
                    className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
                  >
                    {visibleArticles.map((article, index) => (
                      <article
                        key={article.id}
                        className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 flex flex-col"
                        style={{ animationDelay: `${index * 60}ms` }}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-golf-orange font-medium">{article.source}</span>
                          <span className="text-gray-500 text-sm">
                            {article.date
                              ? new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                              : ''}
                          </span>
                        </div>

                        <h3 className="text-lg md:text-xl font-bold text-golf-gray mb-4 line-clamp-2 hover:text-golf-orange transition-colors duration-300">
                          {article.title}
                        </h3>

                        <div className="relative mb-6 flex-1">
                          <div className="absolute left-0 top-0 w-1 h-full bg-golf-orange rounded-full" />
                          <blockquote className="pl-6 text-gray-600 italic">
                            "{article.quote}"
                          </blockquote>
                        </div>

                        <a
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-golf-orange hover:text-golf-green transition-colors font-medium group"
                        >
                          Read Full Article
                          <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
                        </a>
                      </article>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-10">
                  <button
                    onClick={prev}
                    disabled={page === 0}
                    className="p-2 rounded-full border border-gray-300 text-gray-500 hover:border-golf-orange hover:text-golf-orange disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>

                  <div className="flex gap-2">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => { setDirection(i > page ? 1 : -1); setPage(i); }}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${i === page ? 'bg-golf-orange w-5' : 'bg-gray-300 hover:bg-gray-400'}`}
                        aria-label={`Page ${i + 1}`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={next}
                    disabled={page === totalPages - 1}
                    className="p-2 rounded-full border border-gray-300 text-gray-500 hover:border-golf-orange hover:text-golf-orange disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No press articles available yet.</p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

export default PressSection;
